/**
 * ShreemTech Universal Admin Panel
 * Copyright (c) 2026 ShreemTech Ltd. All Rights Reserved.
 * PROPRIETARY AND CONFIDENTIAL — TRADE SECRET
 */

import { Session, SessionUser, UserRole } from "../types";

export interface LoginParams {
  username: string;
  password: string;
}

export interface AuthResult {
  success: boolean;
  session?: Session;
  requiresMfa?: boolean;
  error?: string;
}

const MOCK_USERS: Record<string, { password: string; user: SessionUser }> = {
  admin: {
    password: "Temp@457",
    user: { id: "1", email: "admin@shreemtech.io", name: "Super Admin", role: "super_admin" },
  },
  "tenant-admin": {
    password: "Temp@457",
    user: {
      id: "2",
      email: "tenant@customer.io",
      name: "Tenant Admin",
      role: "tenant_admin",
      tenantId: "tenant-001",
    },
  },
  user: {
    password: "Temp@457",
    user: { id: "3", email: "user@customer.io", name: "End User", role: "user", tenantId: "tenant-001" },
  },
};

export const mockAuthClient = {
  login: async (params: LoginParams): Promise<AuthResult> => {
    const record = MOCK_USERS[params.username];
    if (!record) {
      return { success: false, error: "Invalid credentials" };
    }
    if (record.password !== params.password) {
      return { success: false, error: "Invalid credentials" };
    }
    const session: Session = {
      user: record.user,
      accessToken: `mock-token-${record.user.id}-${Date.now()}`,
      expiresAt: Date.now() + 8 * 60 * 60 * 1000,
    };
    return { success: true, session };
  },

  logout: async (): Promise<void> => {
    if (typeof document !== "undefined") {
      document.cookie = "shreem-session=; Max-Age=0; path=/";
    }
  },

  validateToken: async (token: string): Promise<SessionUser | null> => {
    if (!token || !token.startsWith("mock-token-")) return null;
    const parts = token.split("-");
    const userId = parts[2];
    const entry = Object.values(MOCK_USERS).find((u) => u.user.id === userId);
    return entry?.user ?? null;
  },

  getSession: (): Session | null => {
    if (typeof document === "undefined") return null;
    const cookie = document.cookie.split(";").find((c) => c.trim().startsWith("shreem-session="));
    if (!cookie) return null;
    try {
      return JSON.parse(decodeURIComponent(cookie.split("=")[1])) as Session;
    } catch {
      return null;
    }
  },

  setSession: (session: Session): void => {
    if (typeof document === "undefined") return;
    const maxAge = Math.floor((session.expiresAt - Date.now()) / 1000);
    document.cookie = `shreem-session=${encodeURIComponent(JSON.stringify(session))}; Max-Age=${maxAge}; path=/; SameSite=Strict`;
  },

  decodeRole: (token: string): UserRole | null => {
    if (!token.startsWith("mock-token-")) return null;
    const userId = token.split("-")[2];
    const entry = Object.values(MOCK_USERS).find((u) => u.user.id === userId);
    return entry?.user.role ?? null;
  },
};
